/**
 * Phase 6 — Helper-actor surfaces.
 * Used inside HelperInvitationDetail.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";


export function HelperLogHoursForm({ invitationId, invitation, onSaved }) {
  const allowed = invitation && invitation.state === "accepted"
                    && (invitation.work_status === "pending"
                        || invitation.work_status === "active");
  const [outcome, setOutcome] = useState("worked");
  const [hours, setHours] = useState("");
  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!allowed) return null;

  const submit = async () => {
    if (outcome !== "did_not_show" && !hours) {
      toast.error("Hours worked is required");
      return;
    }
    setSubmitting(true);
    try {
      await api(`/helpers/me/invitations/${invitationId}/timesheet`, {
        method: "POST",
        body: {
          outcome,
          hours_worked: outcome === "did_not_show" ? 0 : parseFloat(hours),
          work_started_at: startAt ? new Date(startAt).toISOString() : undefined,
          work_ended_at: endAt ? new Date(endAt).toISOString() : undefined,
        },
      });
      toast.success("Timesheet submitted — awaiting Lead Technician approval.");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.problem?.title || e.message);
    } finally { setSubmitting(false); }
  };

  return (
    <section className="avcp-card p-5 md:col-span-2" data-testid={TID.helperLogHoursForm}>
      <h3 className="font-semibold mb-2">Log your hours</h3>
      <p className="text-xs text-[var(--avcp-ink-soft)] mb-3">
        Submit your hours and the Lead Technician will approve them. If rejected,
        you can resubmit with corrected hours.
      </p>
      <div className="grid sm:grid-cols-2 gap-2">
        <label className="text-sm">
          Outcome
          <select className="avcp-input w-full" value={outcome}
                  data-testid={TID.helperLogOutcome}
                  onChange={(e) => setOutcome(e.target.value)}>
            <option value="worked">Worked</option>
            <option value="walked_off">Walked off</option>
            <option value="did_not_show">Did not show</option>
          </select>
        </label>
        <label className="text-sm">
          Hours worked
          <input type="number" step="0.25" min="0" className="avcp-input w-full"
                  data-testid={TID.helperLogHours}
                  disabled={outcome === "did_not_show"}
                  value={outcome === "did_not_show" ? "0" : hours}
                  onChange={(e) => setHours(e.target.value)} />
        </label>
        <label className="text-sm">
          Work started
          <input type="datetime-local" className="avcp-input w-full"
                  data-testid={TID.helperLogStart}
                  value={startAt} onChange={(e) => setStartAt(e.target.value)} />
        </label>
        <label className="text-sm">
          Work ended
          <input type="datetime-local" className="avcp-input w-full"
                  data-testid={TID.helperLogEnd}
                  value={endAt} onChange={(e) => setEndAt(e.target.value)} />
        </label>
      </div>
      <div className="flex justify-end mt-3">
        <button className="avcp-btn-primary" disabled={submitting}
                data-testid={TID.helperLogSubmit}
                onClick={submit}>
          {submitting ? "Submitting…" : "Submit timesheet"}
        </button>
      </div>
    </section>
  );
}


export function HelperRateLeadForm({ invitationId, invitation, bookingState }) {
  const [score, setScore] = useState(5);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const eligible = bookingState === "completed"
                    && invitation
                    && ["completed", "walked_off"].includes(invitation.work_status);
  if (!eligible) return null;

  const submit = async () => {
    setSubmitting(true);
    try {
      await api(`/helpers/me/invitations/${invitationId}/rating`, {
        method: "POST",
        body: {
          score: parseInt(score, 10),
          comment: comment || undefined,
        },
      });
      toast.success("Rating submitted.");
      setSubmitted(true);
    } catch (e) {
      toast.error(e.problem?.title || e.message);
    } finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <section className="avcp-card p-5 md:col-span-2 bg-[var(--avcp-surface-2)]">
        <p className="text-sm">✓ Thanks — your rating was submitted.</p>
      </section>
    );
  }

  return (
    <section className="avcp-card p-5 md:col-span-2" data-testid={TID.helperRateLeadForm}>
      <h3 className="font-semibold mb-2">Rate your Lead Technician</h3>
      <p className="text-xs text-[var(--avcp-ink-soft)] mb-3">
        Your rating is anonymous to the Lead Technician. Only Workforce Partners admins can
        de-anonymize during a dispute investigation.
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <select className="avcp-input" value={score}
                data-testid={TID.helperRateLeadScore}
                onChange={(e) => setScore(e.target.value)}>
          {[1, 2, 3, 4, 5].map((n) => (
            <option key={n} value={n}>{n} star{n > 1 ? "s" : ""}</option>
          ))}
        </select>
        <input className="avcp-input flex-1 min-w-[180px]"
                placeholder="Optional comment"
                data-testid={TID.helperRateLeadComment}
                value={comment} onChange={(e) => setComment(e.target.value)} />
        <button className="avcp-btn-primary" disabled={submitting}
                data-testid={TID.helperRateLeadSubmit}
                onClick={submit}>{submitting ? "Sending…" : "Submit"}</button>
      </div>
    </section>
  );
}
